import { format } from 'owa-strings/utilities/format';
import { getOwaMiniStrings } from 'owa-strings/owa-mini-strings';
import { listViewStrings } from './constants';
import { MailListItemProps } from '../components/MailListItem';
import { PropertyIcons } from '../schema/PropertyIcons';

const getFirstLine = ({ showDraftsIndicator, firstLineText }: MailListItemProps) =>
    showDraftsIndicator
        ? `${getOwaMiniStrings().draftSenderPersonaPlaceholder}${firstLineText}`
        : firstLineText;

const getSecondLine = ({ secondLineText }: MailListItemProps) => secondLineText;

const getPreviewText = ({ thirdLineText }: MailListItemProps) =>
    thirdLineText || listViewStrings.emptyString;

const getReceivedTimeStamp = ({ lastDeliveryTimestamp }: MailListItemProps) =>
    lastDeliveryTimestamp;

const getReplyForwardedInfo = ({ messageClassIcon }: MailListItemProps) => {
    const strings = getOwaMiniStrings();

    switch (messageClassIcon) {
        case PropertyIcons.MailListViewReplied:
            return strings.replyMessageAriaLabel;
        case PropertyIcons.MailListViewForwarded:
            return strings.forwardedMessageAriaLabel;
    }

    return listViewStrings.emptyString;
};

const getUnreadCountInfo = ({ unreadCount }: MailListItemProps) => {
    const strings = getOwaMiniStrings();

    switch (unreadCount) {
        case 0:
            return listViewStrings.emptyString;
        case 1:
            return strings.unreadMessageAriaLabel;
    }

    return format(strings.unreadMessagesAriaLabel, unreadCount);
};

const getPinnedInfo = ({ isPinned }: MailListItemProps) =>
    isPinned ? getOwaMiniStrings().pinnedMessageAriaLabel : listViewStrings.emptyString;

const getFlaggedInfo = ({ isFlagged }: MailListItemProps) =>
    isFlagged ? getOwaMiniStrings().flaggedMessageAriaLabel : listViewStrings.emptyString;

const getAttachmentInfo = ({ hasAttachment }: MailListItemProps) =>
    hasAttachment ? getOwaMiniStrings().hasAttachmentAriaLabel : listViewStrings.emptyString;

const getMentionedInfo = ({ effectiveMentioned }: MailListItemProps) =>
    effectiveMentioned
        ? getOwaMiniStrings().mentionedMessageAriaLabel
        : listViewStrings.emptyString;

const getImportanceInfo = ({ importance }: MailListItemProps) =>
    importance === listViewStrings.highImportanceMessage
        ? getOwaMiniStrings().highImportanceMessageAriaLabel
        : listViewStrings.emptyString;

const getLikedInfo = ({ likeCount }: MailListItemProps) =>
    likeCount && likeCount > 0
        ? getOwaMiniStrings().likedMessageAriaLabel
        : listViewStrings.emptyString;

const getMeetingSummary = ({ richContents, isMeetingMessage }: MailListItemProps) => {
    const strings = getOwaMiniStrings();
    const isRsvpUnitShown = !!richContents?.RSVPUnit?.Component;
    const meetingSummaryWhenRsvpIsNotShown = isMeetingMessage
        ? strings.meetingRequestAriaLabel
        : listViewStrings.emptyString;
    return isRsvpUnitShown
        ? richContents?.RSVPUnit?.ariaDetails || listViewStrings.emptyString
        : meetingSummaryWhenRsvpIsNotShown;
};

const getFolderLabelInfo = ({ parentFolderName }: MailListItemProps) => {
    const strings = getOwaMiniStrings();
    return parentFolderName
        ? format(strings.mailListItemFolderInfoAriaLabel, parentFolderName)
        : '';
};

const getAriaSummaryTemplate = ({
    richContents,
    isInCheckedMode,
    isSelected,
}: MailListItemProps) => {
    const strings = getOwaMiniStrings();
    const isRsvpUnitShown = !!richContents?.RSVPUnit?.Component;

    if (isSelected) {
        return isRsvpUnitShown
            ? strings.selectedMailItemWithRsvpSummarySelectionModeAriaLabel
            : strings.selectedMailItemSummarySelectionModeAriaLabel;
    }

    if (isInCheckedMode) {
        return isRsvpUnitShown
            ? strings.mailItemWithRsvpSummarySelectionModeAriaLabel
            : strings.mailItemSummarySelectionModeAriaLabel;
    }

    return isRsvpUnitShown
        ? strings.mailItemWithRsvpSummaryAriaLabel
        : strings.mailItemSummaryAriaLabel;
};

/**
 * @returns the localized aria label for each item in the list view
 * @param mailListItemDataProps props for MailListItem
 */
export const getAriaSummaryLabel = (props: MailListItemProps): string =>
    format(
        getAriaSummaryTemplate(props),
        getFirstLine(props),
        getSecondLine(props),
        getReceivedTimeStamp(props),
        getFolderLabelInfo(props),
        getReplyForwardedInfo(props),
        getUnreadCountInfo(props),
        getPinnedInfo(props),
        getFlaggedInfo(props),
        getMentionedInfo(props),
        getAttachmentInfo(props),
        getImportanceInfo(props),
        getLikedInfo(props),
        getMeetingSummary(props),
        getPreviewText(props)
    );
